import React, { useMemo, useState, useEffect } from "react";
import { ArrowRight, Filter, Layers, Target, ExternalLink, Sparkles, CheckCircle2, TrendingUp, Users, Award, Clock, ChevronRight, Zap, Star } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { projectsService } from "@/api/projectsService";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ProjectCardSkeleton } from "@/components/ui/ProjectCardSkeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";

import { Reveal } from "@/components/site/SiteLayout";

function Glass({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-shadow duration-200 hover:shadow-[0_0_26px_rgba(0,194,255,0.3)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get("project");

  const [openDialogId, setOpenDialogId] = useState(projectIdParam || null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    projectsService
      .getPublishedProjects()
      .then((data) => {
        if (mounted) {
          setItems(data || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load projects catalog:", err);
        if (mounted) {
          setItems([]);
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (projectIdParam) {
      setOpenDialogId(projectIdParam);
      setTimeout(() => {
        const el =
          document.getElementById(`project-${projectIdParam}`) ||
          document.querySelector(`[data-project-id="${projectIdParam}"]`) ||
          document.querySelector(`[data-slug="${projectIdParam}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 150);
    }
  }, [projectIdParam]);

  const categories = useMemo(() => {
    const all = Array.from(new Set(items.map((x) => x.category)));
    return ["All", ...all];
  }, [items]);

  const [filter, setFilter] = useState("All");

  const filtered = useMemo(() => {
    if (filter === "All") return items;
    return items.filter((x) => x.category === filter);
  }, [items, filter]);

  return (
    <div className="w-full px-5 py-16 xl:px-10 2xl:px-14">
      {/* Hero Section */}
      <div className="relative">
        <Reveal delay={0}>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent px-5 py-2.5 text-sm text-white backdrop-blur-xl">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="font-medium">Our Work</span>
          </div>
        </Reveal>

        <Reveal delay={90}>
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl">
            Projects That{" "}
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              Drive Results
            </span>
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white/70">
            Explore our portfolio of successful digital transformations. Each project showcases our commitment to delivering
            innovative solutions that solve real business challenges and create measurable impact.
          </p>
        </Reveal>

        {/* Stats Section */}
        <Reveal delay={150}>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4 lg:w-3/4">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-5 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-2.5">
                  <Award className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">10+</div>
                  <div className="text-xs text-white/60">Projects Delivered</div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-5 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-green-500/10 p-2.5">
                  <Users className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">5+</div>
                  <div className="text-xs text-white/60">Happy Clients</div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-5 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-blue-500/10 p-2.5">
                  <TrendingUp className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">100%</div>
                  <div className="text-xs text-white/60">Success Rate</div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-5 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-purple-500/10 p-2.5">
                  <Zap className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">24/7</div>
                  <div className="text-xs text-white/60">Support</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <Separator className="my-12 bg-white/10" />

      <Reveal delay={150}>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-transparent">
              <Filter className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-base font-semibold text-white">Filter Projects</div>
              <div className="text-sm text-white/60">Explore by category</div>
            </div>
          </div>

          <ToggleGroup
            type="single"
            value={filter}
            onValueChange={(v) => setFilter(v || "All")}
            className="flex flex-wrap justify-start gap-2.5 lg:justify-end"
          >
            {categories.map((c) => (
              <ToggleGroupItem
                key={c}
                value={c}
                className={cn(
                  "rounded-full border border-white/15 bg-white/[0.05] px-5 py-2.5 text-sm font-medium text-white/75",
                  "transition-all duration-300 hover:bg-white/[0.08] hover:border-white/20",
                  "data-[state=on]:bg-gradient-to-r data-[state=on]:from-primary/20 data-[state=on]:to-primary/10",
                  "data-[state=on]:border-primary/40 data-[state=on]:text-white data-[state=on]:shadow-lg data-[state=on]:shadow-primary/20",
                )}
              >
                {c}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </Reveal>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <ProjectCardSkeleton key={`projects-skeleton-${idx}`} />
          ))
        ) : filtered.length > 0 ? (
          filtered.map((cs, idx) => (
          <Reveal key={cs.id} delay={60 + idx * 70}>
            <div
              id={`project-${cs.id}`}
              data-project-id={cs.id}
              data-slug={cs.slug}
              className="group relative h-full"
            >
              {/* Glow effect on hover */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-primary/20 via-primary/10 to-transparent opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />

              <Glass className="relative h-full p-6 transition-all duration-300 group-hover:border-primary/30">
                <div className="flex flex-col h-full gap-5">
                  {/* Header */}
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        <span className="text-xs font-medium text-primary">{cs.category}</span>
                      </div>
                      <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-transparent transition-colors duration-300 group-hover:border-primary/30 group-hover:bg-primary/10">
                        <Layers className="h-4 w-4 text-primary" />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-white transition-colors duration-300 group-hover:text-primary">
                      {cs.title}
                    </h3>

                    {cs.client && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-white/60">
                        <Users className="h-3.5 w-3.5" />
                        <span>Client: {cs.client}</span>
                      </div>
                    )}
                  </div>

                  {/* Problem Card */}
                  <div className="rounded-xl border border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-transparent p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-orange-400" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-orange-400">Challenge</span>
                      </div>
                      {cs.problem.length > 115 && (
                        <button
                          type="button"
                          onClick={() => setOpenDialogId(cs.id)}
                          className="text-[11px] font-medium text-orange-400 hover:text-orange-300 transition-colors cursor-pointer hover:underline inline-flex items-center gap-0.5"
                          title="View case study for full challenge"
                        >
                          <span>more</span>
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    <p
                      onClick={() => cs.problem.length > 115 && setOpenDialogId(cs.id)}
                      className={cn(
                        "text-sm leading-relaxed text-white/70 line-clamp-3 h-[4.5rem] overflow-hidden",
                        cs.problem.length > 115 && "cursor-pointer hover:text-white/90 transition-colors"
                      )}
                    >
                      {cs.problem}
                    </p>
                  </div>

                  {/* Solution Card */}
                  <div className="rounded-xl border border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-green-400">Solution</span>
                      </div>
                      {cs.Services.length > 115 && (
                        <button
                          type="button"
                          onClick={() => setOpenDialogId(cs.id)}
                          className="text-[11px] font-medium text-green-400 hover:text-green-300 transition-colors cursor-pointer hover:underline inline-flex items-center gap-0.5"
                          title="View case study for full solution"
                        >
                          <span>more</span>
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    <p
                      onClick={() => cs.Services.length > 115 && setOpenDialogId(cs.id)}
                      className={cn(
                        "text-sm leading-relaxed text-white/70 line-clamp-3 h-[4.5rem] overflow-hidden",
                        cs.Services.length > 115 && "cursor-pointer hover:text-white/90 transition-colors"
                      )}
                    >
                      {cs.Services}
                    </p>
                  </div>

                  {/* Tech Stack */}
                  <div>
                    <div className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-white/50">Tech Stack</div>
                    <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                      {cs.stack.slice(0, 3).map((t) => (
                        <Badge
                          key={t}
                          className="rounded-lg border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 whitespace-nowrap shrink-0"
                        >
                          {t}
                        </Badge>
                      ))}
                      {cs.stack.length > 3 && (
                        <button
                          type="button"
                          onClick={() => setOpenDialogId(cs.id)}
                          className="rounded-lg border border-primary/40 bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/25 hover:border-primary/60 transition-colors whitespace-nowrap shrink-0 cursor-pointer"
                          title="View case study to see all technologies"
                        >
                          +{cs.stack.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Outcome */}
                  {cs.outcome && (
                    <div className="rounded-xl border border-white/10 bg-gradient-to-br from-primary/5 to-transparent p-4">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-primary" />
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary">Impact</span>
                        </div>
                        {cs.outcome.length > 115 && (
                          <button
                            type="button"
                            onClick={() => setOpenDialogId(cs.id)}
                            className="text-[11px] font-medium text-primary/90 hover:text-primary transition-colors cursor-pointer hover:underline inline-flex items-center gap-0.5"
                            title="View case study for full impact"
                          >
                            <span>more</span>
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <p
                        onClick={() => cs.outcome.length > 115 && setOpenDialogId(cs.id)}
                        className={cn(
                          "text-sm leading-relaxed text-white/70 line-clamp-3 h-[4.5rem] overflow-hidden",
                          cs.outcome.length > 115 && "cursor-pointer hover:text-white/90 transition-colors"
                        )}
                      >
                        {cs.outcome}
                      </p>
                    </div>
                  )}

                  {/* Status Badge */}
                  {cs.status && cs.status.toLowerCase() !== "completed" && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-yellow-400" />
                      <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/10">
                        {cs.status}
                      </Badge>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-auto pt-2 flex gap-2.5">
                    {cs.url && (
                      <Button
                        variant="secondary"
                        onClick={() => window.open(cs.url, "_blank")}
                        className="flex-1 rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10 hover:border-primary/30 transition-all duration-300 group/btn"
                      >
                        <span>View Live</span>
                        <ExternalLink className="ml-2 h-3.5 w-3.5 transition-transform group-hover/btn:scale-110" />
                      </Button>
                    )}
                    <Dialog open={openDialogId === cs.id || openDialogId === cs.slug} onOpenChange={(open) => setOpenDialogId(open ? (cs.slug || cs.id) : null)}>
                      <DialogTrigger asChild>
                        <Button
                          variant="secondary"
                          onClick={() => setOpenDialogId(cs.id)}
                          className={cn(
                            "rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent text-white hover:from-primary/20 hover:to-primary/5 transition-all duration-300 group/btn",
                            cs.url ? 'flex-1' : 'w-full'
                          )}
                        >
                          <span>Case Study</span>
                          <ChevronRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl border-white/10 bg-black text-white overflow-hidden flex flex-col max-h-[80vh] p-0">
                        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-white/10">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/20 to-transparent flex items-center justify-center">
                              <Target className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <DialogTitle className="text-2xl font-bold text-white">{cs.title}</DialogTitle>
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
                                  {cs.category}
                                </Badge>
                                {cs.client && (
                                  <span className="text-sm text-white/60">• Client: {cs.client}</span>
                                )}
                                {cs.status && cs.status.toLowerCase() !== "completed" && (
                                  <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/10">
                                    {cs.status}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </DialogHeader>

                        <div className="overflow-y-auto px-6 py-6 flex-1 max-h-[calc(80vh-120px)] scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30">
                          <div className="space-y-6">
                            {/* Project Overview Card */}
                            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-6">
                              <div className="mb-4 flex items-center gap-2.5">
                                <div className="rounded-lg bg-blue-500/10 p-2">
                                  <Sparkles className="h-5 w-5 text-blue-400" />
                                </div>
                                <span className="text-sm font-bold uppercase tracking-wider text-blue-400">Project Overview</span>
                              </div>
                              <div className="grid gap-4 md:grid-cols-2">
                                {cs.client && (
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 rounded-lg bg-primary/10 p-2">
                                      <Users className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                      <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">Client</div>
                                      <div className="text-base text-white font-medium">{cs.client}</div>
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5 rounded-lg bg-purple-500/10 p-2">
                                    <Layers className="h-4 w-4 text-purple-400" />
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">Category</div>
                                    <div className="text-base text-white font-medium">{cs.category}</div>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5 rounded-lg bg-green-500/10 p-2">
                                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">Status</div>
                                    <div className="text-base text-white font-medium">{cs.status || "Completed"}</div>
                                  </div>
                                </div>
                                {cs.team_size && (
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 rounded-lg bg-blue-500/10 p-2">
                                      <Award className="h-4 w-4 text-blue-400" />
                                    </div>
                                    <div>
                                      <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">Team Size</div>
                                      <div className="text-base text-white font-medium">{cs.team_size}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Challenge Section */}
                            {cs.problem && (
                              <div className="rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-transparent p-6">
                                <div className="mb-4 flex items-center gap-2.5">
                                  <div className="rounded-lg bg-orange-500/10 p-2">
                                    <Target className="h-5 w-5 text-orange-400" />
                                  </div>
                                  <span className="text-sm font-bold uppercase tracking-wider text-orange-400">The Challenge</span>
                                </div>
                                <p className="text-base leading-relaxed text-white/80 whitespace-pre-line">
                                  {cs.problem}
                                </p>
                              </div>
                            )}

                            {/* Solution Section */}
                            {cs.Services && (
                              <div className="rounded-2xl border border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent p-6">
                                <div className="mb-4 flex items-center gap-2.5">
                                  <div className="rounded-lg bg-green-500/10 p-2">
                                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                                  </div>
                                  <span className="text-sm font-bold uppercase tracking-wider text-green-400">Our Engineering Solution</span>
                                </div>
                                <p className="text-base leading-relaxed text-white/80 whitespace-pre-line">
                                  {cs.Services}
                                </p>
                              </div>
                            )}

                            {/* Impact Section */}
                            {cs.outcome && (
                              <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-6">
                                <div className="mb-4 flex items-center gap-2.5">
                                  <div className="rounded-lg bg-primary/10 p-2">
                                    <TrendingUp className="h-5 w-5 text-primary" />
                                  </div>
                                  <span className="text-sm font-bold uppercase tracking-wider text-primary">Business Impact</span>
                                </div>
                                <p className="text-base leading-relaxed text-white/80 whitespace-pre-line">
                                  {cs.outcome}
                                </p>
                              </div>
                            )}

                            {/* Measurable Results - ONLY rendered when verified metrics exist in database */}
                            {Array.isArray(cs.metrics) && cs.metrics.length > 0 && (
                              <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-6">
                                <div className="mb-4 flex items-center gap-2.5">
                                  <div className="rounded-lg bg-primary/10 p-2">
                                    <Sparkles className="h-5 w-5 text-primary" />
                                  </div>
                                  <span className="text-sm font-bold uppercase tracking-wider text-primary">Measurable Results</span>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                  {cs.metrics.map((m, mIdx) => (
                                    <div key={m.id || mIdx} className="rounded-lg border border-primary/10 bg-primary/[0.02] p-4">
                                      <div className="text-2xl font-bold text-primary mb-1">{m.value}</div>
                                      <div className="text-sm text-white font-medium">{m.label}</div>
                                      {m.desc && <div className="text-xs text-white/60 mt-0.5">{m.desc}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Tech Stack Section */}
                            {Array.isArray(cs.stack) && cs.stack.length > 0 && (
                              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                                <div className="mb-4 flex items-center gap-2.5">
                                  <div className="rounded-lg bg-purple-500/10 p-2">
                                    <Layers className="h-5 w-5 text-purple-400" />
                                  </div>
                                  <span className="text-sm font-bold uppercase tracking-wider text-purple-400">Technology Stack</span>
                                </div>
                                <div>
                                  <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Technologies Used</div>
                                  <div className="flex flex-wrap gap-2.5">
                                    {cs.stack.map((t) => (
                                      <Badge
                                        key={t}
                                        className="rounded-lg border-primary/20 bg-primary/5 text-primary px-3 py-1.5 text-sm hover:bg-primary/10"
                                      >
                                        {t}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Client Testimonial (ONLY rendered when verified quote exists in database) */}
                            {cs.testimonial_quote && (
                              <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-6">
                                <div className="mb-4 flex items-center justify-between">
                                  <div className="flex items-center gap-2.5">
                                    <div className="rounded-lg bg-primary/10 p-2">
                                      <Users className="h-5 w-5 text-primary" />
                                    </div>
                                    <span className="text-sm font-bold uppercase tracking-wider text-primary">Client Feedback</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-amber-400">
                                    {Array.from({ length: cs.testimonial_rating || 5 }).map((_, starIdx) => (
                                      <Star key={starIdx} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                                    ))}
                                  </div>
                                </div>
                                <div className="relative">
                                  <div className="absolute -top-2 -left-2 text-6xl text-primary/20 font-serif">"</div>
                                  <p className="text-base leading-relaxed text-white/80 italic pl-8">
                                    {cs.testimonial_quote}
                                  </p>
                                  <div className="mt-4 flex items-center gap-3 pl-8">
                                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 flex items-center justify-center">
                                      <span className="text-sm font-bold text-primary">
                                        {(cs.testimonial_author || cs.client || "C").charAt(0)}
                                      </span>
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-white">
                                        {cs.testimonial_author || cs.client}
                                      </div>
                                      <div className="text-xs text-white/60">
                                        {cs.testimonial_role || `${cs.category} Client`}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Status Section (moved here) */}
                            {cs.status && cs.status.toLowerCase() === "ongoing" && (
                              <div className="flex items-center gap-3 rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-5">
                                <Clock className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-white mb-1">Project Status</div>
                                  <p className="text-sm text-white/70">
                                    This project is currently ongoing. We're actively working on implementing
                                    additional features and ensuring optimal performance before final delivery.
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* CTA Section */}
                            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex flex-wrap items-center gap-2.5">
                                {cs.url && (
                                  <Button
                                    onClick={() => window.open(cs.url, "_blank")}
                                    variant="secondary"
                                    className="rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10 hover:border-primary/30 h-12 px-5"
                                  >
                                    <span>Visit Live Site</span>
                                    <ExternalLink className="ml-2 h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  onClick={() => navigate(`/projects/${cs.slug || cs.id}`)}
                                  variant="outline"
                                  className="rounded-xl border-white/15 bg-white/[0.03] text-white hover:bg-white/10 h-12 px-5 text-xs font-semibold"
                                >
                                  <span>Full Case Study Page</span>
                                  <ExternalLink className="ml-2 h-3.5 w-3.5 text-primary" />
                                </Button>
                              </div>
                              <Button
                                onClick={() => navigate("/contact#form")}
                                className="h-12 rounded-xl bg-primary text-black btn-glow hover:bg-primary/90 px-6 font-semibold"
                              >
                                Start Similar Project
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </Glass>
            </div>
          </Reveal>
        ))
        ) : (
          <div className="col-span-full py-16 text-center">
            <p className="text-white/60 text-sm">No projects found for the selected category.</p>
          </div>
        )}
      </div>

      <Separator className="my-16 bg-white/10" />

      {/* CTA Section */}
      <Reveal delay={220}>
        <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-10 backdrop-blur-xl">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 opacity-50" />
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary mb-4">
                  <Sparkles className="h-3.5 w-3.5" />
                  Let's Build Together
                </div>
                <h2 className="text-3xl font-bold text-white mb-3 lg:text-4xl">
                  Ready to Start Your Project?
                </h2>
                <p className="text-base text-white/70 max-w-2xl leading-relaxed">
                  Transform your vision into reality with our expert team. We specialize in delivering innovative solutions
                  that drive measurable business results. Let's discuss your project today.
                </p>

                {/* Benefits */}
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex items-center gap-2.5 text-sm text-white/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span>Dedicated team of experts</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-white/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span>Agile development process</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-white/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span>Transparent communication</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <Button
                  onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                  className="h-14 rounded-xl bg-primary px-8 text-black btn-glow hover:bg-primary/90 font-semibold text-base group"
                >
                  Schedule a Call
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  onClick={() => navigate("/contact#form")}
                  variant="secondary"
                  className="h-14 rounded-xl border-2 border-white/15 bg-white/[0.06] text-white hover:bg-white/10 hover:border-primary/30 px-8 font-semibold text-base"
                >
                  Get a Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
